import {
  Component,
  inject,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { OutreachLink } from '../../models/outreach-link';
import { takeUntil } from 'rxjs';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { QRCodeModule } from 'angularx-qrcode';
import { FormsModule } from '@angular/forms';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { PublicLinkService } from '../../services/public-link.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { Router } from '@angular/router';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-outreach-link-details',
  standalone: true,
  imports: [
    SecondaryPopupComponent,
    CommonModule,
    SvgIconComponent,
    QRCodeModule,
    FormsModule,
    PopupComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './outreach-link-details.component.html',
  styleUrl: './outreach-link-details.component.scss',
})
export class OutreachLinkDetailsComponent
  extends DestroyBase
  implements OnInit
{
  @Input({ required: true }) outreachLinkId!: number;
  @Output() popupClosed = new EventEmitter<boolean>();
  @Output() linkDeleted = new EventEmitter<number>();
  @Output() linkStatusChanged = new EventEmitter<number>();
  @Output() linkUpdated = new EventEmitter<OutreachLink>();
  @ViewChild('qrCodeCanvas') qrCodeCanvas!: ElementRef;

  publicLinkService = inject(PublicLinkService);
  clipboard = inject(Clipboard);
  imageService = inject(ImageService);
  router = inject(Router);
  auditLogService = inject(AuditLogService);

  connectionLogoUrl: string | null = null;

  outreachLinkDetails: OutreachLink | null = null;
  loading = false;
  error: string | null = null;
  disableEnableText = '';

  // Copy status for each link type
  linkCopySuccess = false;
  qrCodeCopySuccess = false;

  // Edit functionality
  isEditingName = false;
  editedName = '';
  isSaving = false;
  showDeleteLinkPopup = false;
  showDisableLinkPopup = false;
  showLanguageSelectionPopup = false;

  submissionVisibilityEditMode = false;
  submissionVisibility = 1;
  // Language options for QR code printing
  languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
  ];

  ngOnInit(): void {
    this.fetchOutreachLinkDetails();

    this.imageService.connectionImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.connectionLogoUrl = url;
      });
  }

  closePopup(isClosed: boolean): void {
    this.popupClosed.emit(isClosed);
  }

  getObjectKeys(obj: Record<string, string> | null | undefined): string[] {
    return Object.keys(obj || {});
  }

  deleteLink(): void {
    this.auditLogService.logFeature({
      featureName: 'outreach_link',
      action: 'delete',
      meta: {
        linkId: String(this.outreachLinkId),
        linkName: this.outreachLinkDetails?.name || '',
      },
      description: 'User deletes an outreach link',
    });

    this.publicLinkService
      .deleteOutreachLink(this.outreachLinkId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.linkDeleted.emit(this.outreachLinkId);
          this.closePopup(true);
        },
        error: (err) => {
          console.error('Error deleting link:', err);
          this.error = 'Failed to delete the link. Please try again later.';
        },
      });
  }

  disableEnableLink(): void {
    const isDisabling = this.outreachLinkDetails?.is_active;
    this.auditLogService.logFeature({
      featureName: 'outreach_link',
      action: isDisabling ? 'disable' : 'enable',
      meta: {
        linkId: String(this.outreachLinkId),
        linkName: this.outreachLinkDetails?.name || '',
      },
      description: isDisabling
        ? 'User disables an outreach link'
        : 'User enables an outreach link',
    });

    this.publicLinkService
      .UpdateOutreachLink(
        this.outreachLinkId,
        undefined,
        !this.outreachLinkDetails?.is_active,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.linkStatusChanged.emit(this.outreachLinkId);
          if (this.outreachLinkDetails) {
            this.outreachLinkDetails.is_active =
              !this.outreachLinkDetails.is_active;
          }
          this.disableEnableText = this.outreachLinkDetails?.is_active
            ? 'Disable'
            : 'Enable';
          this.showDisableLinkPopup = false;
        },
        error: (err) => {
          console.error('Error disabling/enabling link:', err);
          this.error =
            'Failed to disable/enable the link. Please try again later.';
        },
      });
  }

  getLinkUrl(type: string): string {
    if (!this.outreachLinkDetails) return '';
    return `${window.location.origin}/link-callback/${this.outreachLinkDetails.unique_id}?access_type=${type}`;
  }

  copyToClipboard(text: string, type: 'link' | 'qr'): void {
    this.clipboard.copy(text);
    if (type === 'link') {
      this.linkCopySuccess = true;
      setTimeout(() => {
        this.linkCopySuccess = false;
      }, 2000);
    }
    if (type === 'qr') {
      this.qrCodeCopySuccess = true;
      setTimeout(() => {
        this.qrCodeCopySuccess = false;
      }, 2000);
    }
  }

  hasAccessType(): boolean {
    // Always return true to show both link and QR code sections
    return true;
  }

  getViewsCount(type: string): number {
    // If access_types doesn't exist or is empty, return 0
    if (
      !this.outreachLinkDetails?.access_types ||
      this.outreachLinkDetails.access_types.length === 0
    ) {
      return 0;
    }

    const accessType = this.outreachLinkDetails.access_types.find(
      (at) => at.access_type === type,
    );
    return accessType?.views || 0;
  }

  startEditName(): void {
    if (!this.outreachLinkDetails) return;
    this.isEditingName = true;
    this.editedName = this.outreachLinkDetails.name;
  }

  cancelEditName(): void {
    this.isEditingName = false;
    this.editedName = '';
  }

  saveEditName(): void {
    if (!this.outreachLinkDetails || !this.editedName.trim()) return;

    this.isSaving = true;
    this.publicLinkService
      .UpdateOutreachLink(this.outreachLinkDetails.id, this.editedName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.outreachLinkDetails) {
            this.outreachLinkDetails = {
              ...this.outreachLinkDetails,
              name: this.editedName.trim(),
            };
            this.isEditingName = false;
            this.editedName = '';
            this.isSaving = false;
            this.linkUpdated.emit(this.outreachLinkDetails);
          }
        },
        error: (err) => {
          console.error('Error updating link name:', err);
          this.isSaving = false;
          // Could add error handling here
        },
      });
  }

  editSubmissionVisibility() {
    if (!this.outreachLinkDetails) return;
    this.submissionVisibilityEditMode = true;
    this.submissionVisibility = this.outreachLinkDetails.submission_visibility;
  }

  cancelEditSubmissionVisibility(): void {
    this.submissionVisibilityEditMode = false;
    this.submissionVisibility = 1;
  }

  saveEditSubmissionVisibility(): void {
    if (!this.outreachLinkDetails || !this.submissionVisibility) return;

    this.isSaving = true;
    this.publicLinkService
      .UpdateOutreachLink(
        this.outreachLinkDetails.id,
        undefined,
        undefined,
        this.submissionVisibility,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.outreachLinkDetails) {
            this.outreachLinkDetails = {
              ...this.outreachLinkDetails,
              submission_visibility: this.submissionVisibility,
            };
            this.submissionVisibilityEditMode = false;
            this.submissionVisibility = 1;
            this.isSaving = false;
            this.linkUpdated.emit(this.outreachLinkDetails);
          }
        },
        error: (err) => {
          console.error('Error updating submission visibility:', err);
          this.isSaving = false;
          // Could add error handling here
        },
      });
  }

  printQRCode(): void {
    this.auditLogService.logFeature({
      featureName: 'outreach_link',
      action: 'print',
      meta: {
        linkId: String(this.outreachLinkId),
        linkName: this.outreachLinkDetails?.name || '',
      },
      description: 'User prints outreach link QR code',
    });

    // Show language selection popup instead of directly printing
    this.showLanguageSelectionPopup = true;
  }
  printQRCodeWithLanguage(languageCode: string): void {
    this.showLanguageSelectionPopup = false;

    try {
      const qrElement = this.qrCodeCanvas?.nativeElement;
      if (!qrElement) {
        console.error('QR code element not found');
        return;
      }

      const canvas = qrElement.querySelector('canvas');
      if (!canvas) {
        console.error('QR code canvas not found');
        return;
      }

      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.error('Failed to open print window');
        return;
      }

      const qrDataUrl = canvas.toDataURL('image/png');
      const linkName = this.outreachLinkDetails?.name || 'Outreach Link';
      const qrLogoUrl = `${window.location.origin}/assets/deeproots-logo-v2.jpg`;

      const connectionLogoSrc = this.connectionLogoUrl || '';

      // Get content based on selected language
      const content = this.getContentByLanguage(languageCode);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR Code - ${linkName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
            
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            
            body {
              font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
              background: linear-gradient(135deg, #334ea5 0%, #5a73d8 25%, #7b8ce6 50%, #9aa5f0 75%, #b8bef5 100%);
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
              
            .poster-container {
              width: 100%;
              max-width: 850px;
              background: white;
              border-radius: 20px;
              overflow: hidden;
              box-shadow: 
                0 25px 50px -12px rgba(51, 78, 165, 0.5),
                0 0 0 1px rgba(255, 255, 255, 0.1);
              position: relative;
            }
            
            .poster-header {
              background: linear-gradient(135deg, #334ea5 0%, #5a73d8 100%);
              color: white;
              padding: 25px 25px;
              text-align: center;
              position: relative;
              overflow: hidden;
            }
            
            .poster-header::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: 
                radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
              pointer-events: none;
            }
            
            .poster-title {
              font-size: 32px;
              font-weight: 800;
              margin-bottom: 8px;
              letter-spacing: -0.02em;
              text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
              position: relative;
              z-index: 1;
            }
            
            .poster-subtitle {
              font-size: 18px;
              font-weight: 500;
              opacity: 0.95;
              letter-spacing: -0.01em;
              position: relative;
              z-index: 1;
            }
            
            .poster-content {
              padding: 25px 30px;
            }
            
            .intro-section {
              text-align: center;
              margin-bottom: 25px;
            }
            
            .intro-text {
              font-size: 15px;
              line-height: 1.5;
              color: #1e293b;
              margin-bottom: 20px;
              font-weight: 400;
              max-width: 700px;
              margin-left: auto;
              margin-right: auto;
            }
            
            .benefits-section {
              margin-bottom: 25px;
            }
            
            .benefits-title {
              font-size: 20px;
              font-weight: 700;
              color: #334ea5;
              text-align: center;
              margin-bottom: 20px;
              letter-spacing: -0.02em;
            }
            
            .benefits-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            
            .benefit-card {
              background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              padding: 15px;
              text-align: center;
              transition: all 0.3s ease;
              position: relative;
              overflow: hidden;
            }
            
            .benefit-card::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 3px;
              background: linear-gradient(90deg, #334ea5, #5a73d8);
            }
            
            .benefit-icon {
              width: 40px;
              height: 40px;
              background: linear-gradient(135deg, #334ea5, #5a73d8);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 10px;
              color: white;
              font-size: 18px;
              font-weight: 700;
            }
            
            .benefit-title {
              font-size: 14px;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 5px;
            }
            
            .benefit-description {
              font-size: 11px;
              color: #64748b;
              line-height: 1.4;
            }            
              
            .qr-section {
              background: linear-gradient(135deg, #334ea5 0%, #5a73d8 100%);
              color: white;
              padding: 25px 30px;
              text-align: center;
              margin: -15px -30px -25px;
              position: relative;
              overflow: hidden;
              border-radius: 20px 20px 0 0;
            }
            
            .qr-section::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: 
                radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
              pointer-events: none;
            }
            
            .qr-title {
              font-size: 22px;
              font-weight: 700;
              margin-bottom: 15px;
              text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
              position: relative;
              z-index: 1;
            }
            
            .qr-container {
              display: inline-block;
              background: white;
              padding: 15px;
              border-radius: 16px;
              box-shadow: 
                0 20px 40px -8px rgba(0, 0, 0, 0.4),
                0 0 0 8px rgba(255, 255, 255, 0.1);
              margin: 10px 0;
              position: relative;
              z-index: 1;
            }
              
            .qr-image {
              width: 240px;
              height: 240px;
              border-radius: 12px;
              display: block;
            }
            
            .scan-instruction {
              font-size: 16px;
              font-weight: 600;
              margin-top: 15px;
              text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              position: relative;
              z-index: 1;
            }            
              
            .cta-section {
              background: rgba(255, 255, 255, 0.2);
              backdrop-filter: blur(15px);
              border: 2px solid rgba(255, 255, 255, 0.3);
              border-radius: 16px;
              padding: 18px 25px;
              margin: 20px 25px;
              position: relative;
              z-index: 1;
              box-shadow: 
                0 8px 32px rgba(0, 0, 0, 0.12),
                inset 0 1px 0 rgba(255, 255, 255, 0.4);
            }
              
            .cta-section::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(45deg, 
                rgba(255, 255, 255, 0.1) 0%, 
                rgba(255, 255, 255, 0.05) 100%);
              border-radius: 14px;
              pointer-events: none;
            }
            
            .cta-title {
              font-size: 17px;
              font-weight: 700;
              margin-bottom: 6px;
              text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
              position: relative;
              z-index: 1;
            }
            
            .cta-subtitle {
              font-size: 14px;
              opacity: 0.95;
              font-weight: 400;
              line-height: 1.4;
              position: relative;
              z-index: 1;
            }
            
            .privacy-note {
              background: rgba(255, 255, 255, 0.15);
              border: 1px solid rgba(255, 255, 255, 0.3);
              border-radius: 12px;
              padding: 15px 25px;
              margin: 20px 25px 15px;
              text-align: center;
              font-size: 12px;
              line-height: 1.4;
              color: rgba(255, 255, 255, 0.9);
              font-style: italic;
              position: relative;
              z-index: 1;
              box-shadow: 
                0 4px 16px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.2);
            }
            
            .decorative-elements {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              pointer-events: none;
              overflow: hidden;
            }
            
            .decorative-elements::before,
            .decorative-elements::after {
              content: '';
              position: absolute;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.05);
            }
            
            .decorative-elements::before {
              width: 200px;
              height: 200px;
              top: -100px;
              right: -100px;
            }
            
            .decorative-elements::after {
              width: 150px;
              height: 150px;
              bottom: -75px;
              left: -75px;
            }
            
            @media print {
              body {
                background: white !important;
                margin: 0;
                padding: 0;
                min-height: auto;
              }
              
              .poster-container {
                max-width: none;
                width: 100%;
                box-shadow: none;
                border-radius: 0;
                page-break-inside: avoid;
              }
              
              .poster-header {
                background: linear-gradient(135deg, #334ea5 0%, #5a73d8 100%) !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                padding: 20px 25px !important;
              }
              
              .poster-title {
                font-size: 28px !important;
                margin-bottom: 5px !important;
              }
              
              .poster-subtitle {
                font-size: 16px !important;
              }
              
              .poster-content {
                padding: 10px 25px !important;
              }
              
              .intro-section {
                margin-bottom: 10px !important;
              }
              
              .intro-text {
                font-size: 13px !important;
                margin-bottom: 12px !important;
              }
              
              .benefits-section {
                margin-bottom: 20px !important;
              }
              
              .benefits-title {
                font-size: 15px !important;
                margin-bottom: 12px !important;
              }
              
              .benefits-grid {
                gap: 12px !important;
                margin-bottom: 15px !important;
              }
              
              .benefit-card {
                padding: 12px !important;
                border-radius: 10px !important;
              }
              
              .benefit-icon {
                width: 35px !important;
                height: 35px !important;
                font-size: 16px !important;
                margin-bottom: 8px !important;
              }
              
              .benefit-title {
                font-size: 13px !important;
                margin-bottom: 4px !important;
              }
              
              .benefit-description {
                font-size: 10px !important;
              }              
                
              .qr-section {
                background: linear-gradient(135deg, #334ea5 0%, #5a73d8 100%) !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                padding: 20px 25px !important;
                margin: -15px -25px -20px !important;
                border-radius: 16px 16px 0 0 !important;
              }
              
              .qr-title {
                font-size: 16px !important;
                margin-bottom: 10px !important;
              }
              
              .qr-container {
                padding: 12px !important;
                margin: 8px 0 !important;
                border-radius: 12px !important;
              }
                
              .qr-image {
                width: 180px !important;
                height: 180px !important;
                border-radius: 8px !important;
              }
              
              .scan-instruction {
                font-size: 14px !important;
                margin-top: 12px !important;
              }              
                
              .cta-section {
                background: rgba(255, 255, 255, 0.2) !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                padding: 12px 20px !important;
                margin: 12px 20px !important;
                border-radius: 14px !important;
                border: 2px solid rgba(255, 255, 255, 0.3) !important;
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
              }
              
              .cta-title {
                font-size: 14px !important;
                margin-bottom: 5px !important;
              }
              
              .cta-subtitle {
                font-size: 12px !important;
                margin-bottom: 8px !important;
              }
              
              .privacy-note {
                background: rgba(255, 255, 255, 0.15);
                border: 1px solid rgba(255, 255, 255, 0.3);
                border-radius: 12px;
                padding: 12px 20px;
                margin: 15px 20px 10px;
                text-align: center;
                font-size: 11px;
                line-height: 1.4;
                color: rgba(255, 255, 255, 0.9);
                font-style: italic;
                position: relative;
                z-index: 1;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .benefit-card::before {
                background: linear-gradient(90deg, #334ea5, #5a73d8) !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                height: 2px !important;
              }
              
              .benefit-icon {
                background: linear-gradient(135deg, #334ea5, #5a73d8) !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
            
            @page {
              size: letter;
              margin: 0.4in;
            }

            .logos-container {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 20px;
              padding: 0px 20px;
              background: white;
              border-radius: 12px;
              margin: 15px 25px;
              position: relative;
              z-index: 1;
              box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            }
          </style>
        </head>
        <body>
          <div class="poster-container">
            <div class="poster-header">
              <div class="decorative-elements"></div>
              <h1 class="poster-title">${content.title}</h1>
              <p class="poster-subtitle">${content.subtitle}</p>
            </div>
            
            <div class="poster-content">
              <div class="intro-section">
                <p class="intro-text">
                  ${content.introText}
                </p>
              </div>
              
              <div class="benefits-section">
                <h2 class="benefits-title">${content.benefitsTitle}</h2>
                <div class="benefits-grid">
                  ${content.benefits
                    .map((benefit, index) => {
                      const icons = ['$', '★', '◉'];
                      return `
                    <div class="benefit-card">
                      <div class="benefit-icon">${icons[index] || '●'}</div>
                      <h3 class="benefit-title">${benefit.title}</h3>
                      <p class="benefit-description">${benefit.description}</p>
                    </div>
                  `;
                    })
                    .join('')}
                </div>
              </div>
            </div>
            
            <div class="qr-section">
              <div class="decorative-elements"></div>
              <h2 class="qr-title">${content.qrTitle}</h2>
              
              <div class="qr-container">
                <img src="${qrDataUrl}" alt="QR Code" class="qr-image" />
                <img 
                  src="${qrLogoUrl}" 
                  alt="ACC Logo" 
                  style="
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 40px;
                    height: 40px;
                    background-color: white;
                    border-radius: 50%;
                    padding: 2px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    object-fit: contain;
                    z-index: 10;
                  " 
                />
              </div>
              
              <p class="scan-instruction">${content.scanInstruction}</p>
              
              <div class="cta-section">
                <h3 class="cta-title">${content.ctaTitle}</h3>
                <p class="cta-subtitle">${content.ctaSubtitle}</p>
              </div>
              
              <div class="privacy-note">
                ${content.privacyNote}
              </div>
              
              <div class="logos-container">
                <img src="${window.location.origin}/assets/deeproots.jpg" alt="Deeproots Logo" style="max-width: 140px; height: 60px; border-radius: 12px; object-fit: contain;" />
                <img src="${window.location.origin}/assets/acc-auth0.jpg" alt="ACC Logo" style="max-width: 140px; height: 80px; border-radius: 12px; object-fit: contain;" />
                ${connectionLogoSrc ? `<img src="${connectionLogoSrc}" alt="Partner Logo" style="max-width: 140px; height: 50px; border-radius: 12px; object-fit: contain;" />` : ''}
              </div>
            </div>
          </div>
        </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for the image to load before printing
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };
    } catch (error) {
      console.error('Error printing QR code:', error);
    }
  }

  viewSubmissions(): void {
    if (!this.outreachLinkDetails?.unique_id) return;

    this.router.navigate(['/leads/find'], {
      queryParams: {
        tab: 'deeproots',
        subtab: 'inquiries',
        inquiry_tab: 'link',
        submitted_by_link_or_qr: 'true',
        created_by_link: this.outreachLinkDetails.unique_id,
      },
    });
  }

  getContentByLanguage(languageCode: string) {
    const content = {
      en: {
        title: 'Need Business Support?',
        subtitle: 'ACC Members might help!',
        introText:
          "Community Development Financial Institutions (CDFIs) offer funding, coaching, and support to help entrepreneurs in their Local Community. Whether you're already running a business or just dreaming of one, a CDFI can help you take the next step.",
        benefitsTitle: 'ACC Members can help you with:',
        benefits: [
          {
            title: 'Small Business Financing',
            description:
              'Access to capital and funding solutions tailored to your business needs',
          },
          {
            title: 'New & Existing Business Support',
            description: 'Expert guidance and coaching for all business stages',
          },
          {
            title: 'Connect to trusted local resources',
            description:
              'Connect with trusted partners and resources in your community',
          },
        ],
        qrTitle: 'Connect with a Local Support Partner!',
        scanInstruction: 'Scan to get started!',
        ctaTitle: 'Start or Grow Your Business Right Here!',
        ctaSubtitle:
          'Get funding, coaching, and connections from people who believe in your idea and want to see you succeed.',
        privacyNote:
          'All information provided is encrypted and will be shared solely with ACC Partners for business purposes.',
      },
      es: {
        title: '¿Necesitas Apoyo para tu Negocio?',
        subtitle: '¡Los Miembros de ACC pueden ayudar!',
        introText:
          'Las Instituciones Financieras de Desarrollo Comunitario (CDFI) ofrecen financiamiento, capacitación y apoyo para ayudar a los emprendedores en su Comunidad Local. Ya sea que tengas un negocio funcionando o solo sueñes con uno, una CDFI puede ayudarte a dar el siguiente paso.',
        benefitsTitle: 'Los Miembros de ACC pueden ayudarte con:',
        benefits: [
          {
            title: 'Financiamiento para Pequeñas Empresas',
            description:
              'Acceso a capital y soluciones de financiamiento adaptadas a las necesidades de tu negocio',
          },
          {
            title: 'Apoyo para Negocios Nuevos y Existentes',
            description:
              'Orientación experta y capacitación para todas las etapas del negocio',
          },
          {
            title: 'Conexión con recursos locales confiables',
            description:
              'Conéctate con socios y recursos confiables en tu comunidad',
          },
        ],
        qrTitle: '¡Conéctate con un Socio de Apoyo Local!',
        scanInstruction: '¡Escanea para comenzar!',
        ctaTitle: '¡Comienza o Haz Crecer tu Negocio Aquí Mismo!',
        ctaSubtitle:
          'Obtén financiamiento, capacitación y conexiones de personas que creen en tu idea y quieren verte triunfar.',
        privacyNote:
          'Toda la información proporcionada está encriptada y será compartida únicamente con los Socios de ACC para fines comerciales.',
      },
    };

    return content[languageCode as keyof typeof content] || content.en;
  }

  private fetchOutreachLinkDetails(): void {
    this.loading = true;
    this.error = null;
    this.publicLinkService
      .getOutreachLinkById(this.outreachLinkId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.outreachLinkDetails = data;
          this.disableEnableText = data.is_active ? 'Disable' : 'Enable';
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Error fetching outreach link details';
          this.loading = false;
          console.error('Error fetching outreach link details:', err);
        },
      });
  }
}
